// Build a Takt train FROM a board selection, and line up another selection to an
// existing train. A train element is created per selected activity (codes may
// repeat — the element id is the identity, never the code), and the selection
// becomes the train's first mapped instance. A later selection can only map if
// it lines up 1:1 (same codes, same order) — that strictness IS the reconcile.
import { makeId } from "../../../../utils/id";
import { addTrainDep } from "../../../../models";
import type { TrainCar, TrainDep, TrainInstance, TrainState } from "../../../../models";
import type { SelectionCar, SelectionTie } from "../../../../models";
import type { DepEditOp, DepEditorPredType } from "../../../../models";

export type { SelectionCar, SelectionTie } from "../../../../models";

/** The synthetic leaf key for the pinned train lane (never a real wbs_path). */
export const TRAIN_LEAF_KEY = "__train__";
/** Disjoint negative id space for train-element bars, far below protoNewActs'
 *  -(i+1) range so the two never collide. */
export const TRAIN_ID_BASE = -1_000_000;
export const isTrainCarId = (id: number): boolean => id <= TRAIN_ID_BASE;

/** Re-create EVERY internal train tie on a freshly-placed run — all relationship
 *  types (FS/SS/FF/SF) and EVERY predecessor of a car, mapping carIds → the new
 *  activities' draft ids. Replaces a per-car single-FS-pred scheme that silently
 *  dropped SS/FF/SF ties and second/merge predecessors (e.g. a second lane of
 *  parallel work). Ties whose endpoints weren't both placed are skipped. */
export function placementTieEdits(
  deps: TrainDep[],
  draftByCar: Map<string, string>,
): DepEditOp[] {
  const edits: DepEditOp[] = [];
  for (const d of deps) {
    const pred = draftByCar.get(d.predCarId);
    const succ = draftByCar.get(d.succCarId);
    if (pred == null || succ == null) continue;
    edits.push({
      op: "add",
      pred_activity_id: pred,
      succ_activity_id: succ,
      pred_type: ("PR_" + d.type) as DepEditorPredType,
      lag_hours: Math.round(d.lagDays * 8),
    });
  }
  return edits;
}

/** Create a train: one element per selected activity (in order), the internal
 *  ties mapped to element ids, and the selection recorded as instance #0. */
export function buildTrainFromSelection(
  items: SelectionCar[],
  ties: SelectionTie[],
): TrainState {
  // An uncoded bar carries no marker to map a reusable train by — drop it at this
  // single chokepoint so all callers (previewRefs / handleCreateTrain /
  // buildTrainFromDetection) build a train of CODED cars only. A blank-code car
  // would otherwise make findTrainInLeaf reject every other leaf (byCode skips
  // empty codes) and collapse auto-mapping to source-only.
  const coded = items.filter((it) => (it.code || "").trim() !== "");
  // Anchor the train at day 0: keep the selection's relative layout but shift so
  // the earliest element sits at the left (a clean, pinned start).
  const minStart = coded.length
    ? Math.min(...coded.map((it) => it.startIndex))
    : 0;
  const cars: TrainCar[] = coded.map((it) => {
    const durationDays = Math.max(1, Math.round(it.durationDays) || 1);
    return {
      carId: makeId("car"),
      code: (it.code || "").trim(),
      durationDays,
      baselineDurationDays: durationDays,
      startIndex: Math.max(0, it.startIndex - minStart),
      ...(it.locked ? { locked: true } : {}),
      ...(it.completed ? { completed: true } : {}),
    };
  });
  const carByRef = new Map<string, string>();
  coded.forEach((it, i) => carByRef.set(it.ref, cars[i].carId));

  // At most one tie per element pair, and never a tie that closes a loop — two
  // real ties between the same activities (e.g. an FS and an opposing SS) must
  // not both become train deps, or simulateTrain ratchets off day 0. Route every
  // tie through addTrainDep, the same guard interactive edits use: it dedups by
  // pair AND rejects any edge that would cycle. First acyclic tie for a pair wins.
  let deps: TrainDep[] = [];
  for (const t of ties) {
    const p = carByRef.get(t.predRef);
    const s = carByRef.get(t.succRef);
    if (!p || !s || p === s) continue;
    deps = addTrainDep(deps, p, s, t.type, t.lagDays);
  }

  const instance: TrainInstance = {};
  coded.forEach((it, i) => {
    instance[cars[i].carId] = it.ref;
  });

  // A freshly-captured train has zero edits: baselineDeps mirrors deps so the
  // first Apply is an immediate no-op (only user edits diverge from baseline).
  return {
    cars,
    deps,
    baselineDeps: deps.map((d) => ({ ...d })),
    instances: [instance],
    status: "ready",
    kind: "captured",
  };
}

/** Line a selection up to an existing train. Allowed ONLY on a perfect 1:1
 *  match — same count, same work codes in the same board order. That strictness
 *  is the reconcile: you can't map a chunk that doesn't already match. */
export function lineUpSelectionToTrain(
  train: TrainState,
  items: SelectionCar[],
): { ok: true; instance: TrainInstance } | { ok: false; reason: string } {
  if (items.length !== train.cars.length) {
    return {
      ok: false,
      reason: `Select exactly ${train.cars.length} ${
        train.cars.length === 1 ? "activity" : "activities"
      } that line up with the train — you selected ${items.length}.`,
    };
  }
  for (let i = 0; i < items.length; i++) {
    const got = (items[i].code || "").trim();
    if (got !== train.cars[i].code) {
      return {
        ok: false,
        reason: `Doesn't line up with the train: position ${i + 1} is "${
          got || "(no code)"
        }" but the train has "${train.cars[i].code}". Same codes, same order.`,
      };
    }
  }
  const instance: TrainInstance = {};
  items.forEach((it, i) => {
    instance[train.cars[i].carId] = it.ref;
  });
  return { ok: true, instance };
}

/** Find an occurrence of the train's code MULTISET inside ONE leaf's full work
 *  list, mapping each element to a ref BY CODE — a subset match, order-tolerant
 *  (codes are markers, not identifiers). Greedy: each train car, in canonical
 *  order, claims the earliest still-unclaimed leaf item carrying its code; a
 *  repeated code in the train consumes that many leaf items. Returns the mapped
 *  instance, or null when the leaf lacks the run (so a level that doesn't have
 *  this work is simply skipped). This is how selecting a subset on one level
 *  auto-maps it onto every other level it repeats on. */
export function findTrainInLeaf(
  train: TrainState,
  leafCars: SelectionCar[],
): TrainInstance | null {
  const byCode = new Map<string, SelectionCar[]>();
  for (const c of leafCars) {
    const code = (c.code || "").trim();
    if (!code) continue;
    const q = byCode.get(code);
    if (q) q.push(c);
    else byCode.set(code, [c]);
  }
  // Codes that the leaf carries ONLY as completed bars (no editable occurrence at
  // all). For these, an editable car may bind the completed bar read-only so the
  // leaf's OTHER cars still map. When a code DOES have an editable bar that is
  // merely already-claimed by a competing car, the level is skipped instead — an
  // edit must never strand on a completed bar that has a live sibling.
  const completedOnlyCode = new Set<string>();
  byCode.forEach((q, code) => {
    if (q.every((c) => c.completed === true)) completedOnlyCode.add(code);
  });
  // Assign in TWO PASSES so editable cars win the live bars before locked cars take
  // what remains. With a single greedy pass over train order, an in-progress car
  // appearing before an active car would grab the only live bar and strand the
  // active car's edit (and drop the leaf). Pass 1 = EDITABLE cars (active /
  // not-started), each claiming a live bar; pass 2 = LOCKED cars (in-progress /
  // completed), each claiming a remaining locked/completed bar (or a live one only
  // if nothing locked is left). A lone in-progress car has no editable competitor,
  // so it still falls through to the live bar — preserving that case.
  const instance: TrainInstance = {};
  const editableCars = train.cars.filter((c) => c.locked !== true);
  const lockedCars = train.cars.filter((c) => c.locked === true);
  for (const car of editableCars) {
    const q = byCode.get((car.code || "").trim());
    if (!q || q.length === 0) return null; // this level lacks the run
    // A not-started / active source car prefers an unlocked leaf bar; it may fall
    // back to a remaining in-progress (locked-not-completed) bar — Apply surfaces it
    // read-only — but NEVER a completed one (that would strand its edit on finished
    // work) unless this code exists ONLY as completed bars on the leaf.
    let pick = q.findIndex((c) => !c.completed && !c.locked);
    if (pick < 0) pick = q.findIndex((c) => !c.completed);
    if (pick < 0 && completedOnlyCode.has((car.code || "").trim())) pick = 0;
    if (pick < 0) return null; // a live sibling is spoken for; don't strand on completed work
    instance[car.carId] = (q.splice(pick, 1)[0] as SelectionCar).ref;
  }
  for (const car of lockedCars) {
    const q = byCode.get((car.code || "").trim());
    if (!q || q.length === 0) return null; // this level lacks the run
    const wantCompleted = car.completed === true;
    let pick = -1;
    if (wantCompleted) {
      // A completed source car prefers a completed leaf bar, then any locked bar,
      // then any remaining live bar (bound read-only). A completed car carries no
      // edit, so it only needs an anchor; editable cars already claimed their live
      // bars in pass 1, so a leftover live bar is genuinely spare. Without this the
      // leaf is dropped even though it runs exactly this work.
      pick = q.findIndex((c) => c.completed === true);
      if (pick < 0) pick = q.findIndex((c) => c.locked === true);
      if (pick < 0) pick = q.findIndex((c) => !c.completed);
    } else {
      // An in-progress source car prefers a still-running bar; then any SURPLUS live
      // bar (editable cars already claimed theirs in pass 1, so a leftover live bar
      // is genuinely spare and the in-progress source is itself live work); and only
      // last a completed bar, bound read-only (Apply never re-durations / re-ties a
      // locked ref). Live-before-completed keeps a lone in-progress car on the active
      // bar, while pass-1 ordering means it never steals a live bar an active car needs.
      pick = q.findIndex((c) => c.locked && !c.completed);
      if (pick < 0) pick = q.findIndex((c) => !c.completed);
      if (pick < 0) pick = q.findIndex((c) => c.completed === true);
    }
    if (pick < 0) return null; // this level lacks the run
    instance[car.carId] = (q.splice(pick, 1)[0] as SelectionCar).ref;
  }
  return instance;
}

/** Build a train from a DETECTED run, the SAME way the manual "Create train +
 *  Add to train" path does: one car per activity of a REPRESENTATIVE leaf,
 *  carrying that leaf's REAL internal ties; every other candidate leaf is mapped
 *  by code (order-tolerant). The rep should be a NOT-STARTED-heavy leaf so its
 *  ties live in the dep graph (which omits completed-to-completed edges).
 *
 *  When `spine` is supplied (the detected code spine — a SET, deduped), the
 *  train's cars are built from THAT spine, one car per spine code, resolved from
 *  the rep by code. detectTrains qualifies leaves on the deduped spine, so a rep
 *  with a doubled code would otherwise yield a multiset train that findTrainInLeaf
 *  can't satisfy on a leaf carrying that code only once — dropping a leaf
 *  detection promised. Aligning the train to the spine keeps detection's
 *  "runs on N levels" honest. Without a spine, falls back to the rep's FULL
 *  ordered list (repeats kept) — the manual select-group path.
 *
 *  Returns null if the rep can't be resolved; leaves that don't map are skipped. */
export function buildTrainFromDetection(
  repLeafKey: string,
  leafKeys: string[],
  leafCars: Map<string, SelectionCar[]>,
  tiesForRep: SelectionTie[],
  spine?: string[],
): TrainState | null {
  const repCars = leafCars.get(repLeafKey);
  if (!repCars || repCars.length === 0) return null;
  // Align the rep's selection to the deduped spine (one activity per spine code,
  // in spine order) so the train's code multiset matches what detection qualified.
  const seeded = spine
    ? selectSpineFromLeaf(repCars, spine)
    : { cars: repCars, alias: new Map<string, string>() };
  if (!seeded || seeded.cars.length === 0) return null;
  const seedCars = seeded.cars;
  // A deduped spine code drops the rep's non-seeded occurrence(s); a real internal
  // tie may reference one of those dropped refs (e.g. a completed predecessor of a
  // deduped code drives the next trade). Rewrite each tie's endpoints through the
  // alias so the tie binds to the SEEDED car instead of being stranded as unknown.
  const repTies = tiesForRep.map((t) => ({
    ...t,
    predRef: seeded.alias.get(t.predRef) ?? t.predRef,
    succRef: seeded.alias.get(t.succRef) ?? t.succRef,
  }));
  const train = buildTrainFromSelection(seedCars, repTies);
  const instances = [...train.instances]; // instance #0 = the rep leaf
  for (const key of leafKeys) {
    if (key === repLeafKey) continue;
    const cars = leafCars.get(key);
    if (!cars) continue;
    // Order-tolerant, code-multiset match — the SAME matcher the live Create
    // path uses. detectTrains clusters leaves by code SET in jittered order, so
    // a positional line-up would drop the very leaves detection marked on-train.
    const inst = findTrainInLeaf(train, cars);
    if (inst) instances.push(inst);
  }
  return { ...train, instances };
}

/** Pick one leaf activity per spine code (first unclaimed occurrence), in spine
 *  order — the deduped run the detection qualified the leaf on. Returns null if
 *  the leaf lacks any spine code (so it can't seed the train). `alias` maps every
 *  SKIPPED same-code sibling's ref to the chosen (seeded) ref, so the caller can
 *  rewrite ties that reference a deduped occurrence onto the seeded car. */
function selectSpineFromLeaf(
  leafCars: SelectionCar[],
  spine: string[],
): { cars: SelectionCar[]; alias: Map<string, string> } | null {
  const byCode = new Map<string, SelectionCar[]>();
  for (const c of leafCars) {
    const code = (c.code || "").trim();
    if (!code) continue;
    const q = byCode.get(code);
    if (q) q.push(c);
    else byCode.set(code, [c]);
  }
  // When a code repeats on the rep leaf, seed the spine car from the UNLOCKED
  // occurrence first — so the train element (and thus instance #0) binds to the
  // ACTIVE work Apply can edit, not the completed bar that merely anchors the run.
  const editability = (c: SelectionCar): number =>
    c.completed ? 2 : c.locked ? 1 : 0;
  byCode.forEach((q) => {
    q.sort((a, b) => editability(a) - editability(b));
  });
  const out: SelectionCar[] = [];
  const alias = new Map<string, string>();
  for (const code of spine) {
    const q = byCode.get(code);
    if (!q || q.length === 0) return null;
    const chosen = q.shift() as SelectionCar;
    out.push(chosen);
    for (const dropped of q) alias.set(dropped.ref, chosen.ref);
  }
  return { cars: out, alias };
}
